import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { logo } from "../../Common/CommonImages";

// ── Inline CSS animation keyframes injected once ──────────────────────────────
const injectStyles = () => {
  if (document.getElementById("ap-styles")) return;
  const style = document.createElement("style");
  style.id = "ap-styles";
  style.innerHTML = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

    @keyframes ap-float {
      0%, 100% { transform: translateY(0px); }
      50%       { transform: translateY(-12px); }
    }
    @keyframes ap-pulse-ring {
      0%   { transform: scale(0.95); opacity: 0.7; }
      50%  { transform: scale(1.08); opacity: 0.3; }
      100% { transform: scale(0.95); opacity: 0.7; }
    }
    @keyframes ap-spin-slow {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    @keyframes ap-spin-rev {
      from { transform: rotate(0deg); }
      to   { transform: rotate(-360deg); }
    }
    @keyframes ap-fade-in-up {
      from { opacity: 0; transform: translateY(28px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes ap-dot-blink {
      0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
      40%           { opacity: 1;    transform: scale(1.2); }
    }
    @keyframes ap-particle {
      0%   { transform: translate(0,0)   scale(1);   opacity: 0.8; }
      100% { transform: translate(var(--tx), var(--ty)) scale(0); opacity: 0; }
    }
    @keyframes ap-shimmer {
      0%   { background-position: -400px 0; }
      100% { background-position: 400px 0; }
    }
    @keyframes ap-progress-bar {
      0%   { width: 0%; }
      100% { width: 100%; }
    }
    @keyframes ap-tick-in {
      0%   { stroke-dashoffset: 60; }
      100% { stroke-dashoffset: 0; }
    }
  `;
  document.head.appendChild(style);
};

// ── Animated floating particles ───────────────────────────────────────────────
const Particle = ({ style }) => (
  <div
    style={{
      position: "absolute",
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: "rgba(255,104,18,0.55)",
      animation: "ap-particle 3s ease-out infinite",
      ...style,
    }}
  />
);

// ── Central shield SVG with rings ─────────────────────────────────────────────
function ShieldIcon() {
  return (
    <div style={{ position: "relative", width: 140, height: 140 }}>
      {/* Outer pulse ring */}
      <div
        style={{
          position: "absolute",
          inset: -20,
          borderRadius: "50%",
          border: "2px solid rgba(255,104,18,0.22)",
          animation: "ap-pulse-ring 2.4s ease-in-out infinite",
        }}
      />
      {/* Rotating orbit 1 */}
      <div
        style={{
          position: "absolute",
          inset: -10,
          borderRadius: "50%",
          border: "1.5px dashed rgba(255,104,18,0.38)",
          animation: "ap-spin-slow 10s linear infinite",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: -5,
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: "#FF6812",
            transform: "translateY(-50%)",
            boxShadow: "0 0 8px rgba(255,104,18,0.7)",
          }}
        />
      </div>
      {/* Rotating orbit 2 (reversed) */}
      <div
        style={{
          position: "absolute",
          inset: 6,
          borderRadius: "50%",
          border: "1.5px dashed rgba(255,104,18,0.18)",
          animation: "ap-spin-rev 14s linear infinite",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -4,
            left: "50%",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#ff9a5c",
            transform: "translateX(-50%)",
          }}
        />
      </div>
      {/* Main circle */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #fff5f0 0%, #ffe8d6 100%)",
          boxShadow:
            "0 12px 40px rgba(255,104,18,0.22), 0 4px 16px rgba(255,104,18,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          animation: "ap-float 3.5s ease-in-out infinite",
        }}
      >
        {/* Shield SVG */}
        <svg width="62" height="72" viewBox="0 0 62 72" fill="none">
          <path
            d="M31 2L6 12V34C6 50.5 16.5 65.5 31 70C45.5 65.5 56 50.5 56 34V12L31 2Z"
            fill="url(#shieldGrad)"
            stroke="#FF6812"
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
          {/* Lock body */}
          <rect
            x="21"
            y="36"
            width="20"
            height="16"
            rx="3"
            fill="white"
            opacity="0.95"
          />
          {/* Lock shackle */}
          <path
            d="M25 36V30C25 26.686 27.686 24 31 24C34.314 24 37 26.686 37 30V36"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            opacity="0.95"
          />
          {/* Keyhole */}
          <circle cx="31" cy="42.5" r="2.5" fill="#FF6812" />
          <rect x="30" y="44" width="2" height="4" rx="1" fill="#FF6812" />
          <defs>
            <linearGradient id="shieldGrad" x1="6" y1="2" x2="56" y2="70">
              <stop offset="0%" stopColor="#FF6812" />
              <stop offset="100%" stopColor="#e84d00" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
}

// ── Animated dots row ─────────────────────────────────────────────────────────
function BounceDots() {
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        justifyContent: "center",
        marginTop: 4,
      }}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            background: "#FF6812",
            animation: `ap-dot-blink 1.4s ease-in-out ${i * 0.2}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

// ── Shimmer badge ─────────────────────────────────────────────────────────────
function ShimmerBadge({ text }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 14px",
        borderRadius: 20,
        background:
          "linear-gradient(90deg, #fff3ec 0%, #ffe0cc 50%, #fff3ec 100%)",
        backgroundSize: "800px 100%",
        animation: "ap-shimmer 2.2s linear infinite",
        border: "1px solid rgba(255,104,18,0.3)",
        fontSize: 11,
        fontWeight: 700,
        color: "#cc4400",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: "#FF6812",
          display: "inline-block",
        }}
      />
      {text}
    </div>
  );
}

// ── Info row ──────────────────────────────────────────────────────────────────
function InfoRow({ icon, text }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "9px 14px",
        background: "#fff",
        borderRadius: 10,
        border: "1px solid #f0f0f0",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 9,
          background: "linear-gradient(135deg, #fff3ec, #ffe0cc)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontSize: 17,
        }}
      >
        {icon}
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 13.5,
          color: "#555",
          lineHeight: 1.55,
          fontFamily: "Inter, sans-serif",
        }}
      >
        {text}
      </p>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
function AssessmentPaused() {
  const testData = useSelector((state) => state.testReducer);
  const [mounted, setMounted] = useState(false);
  const companyName = testData?.companyName || "The company";

  useEffect(() => {
    injectStyles();
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  const particles = [
    {
      top: "14%",
      left: "8%",
      "--tx": "30px",
      "--ty": "-50px",
      animationDelay: "0s",
    },
    {
      top: "20%",
      right: "6%",
      "--tx": "-20px",
      "--ty": "-40px",
      animationDelay: "0.6s",
    },
    {
      top: "70%",
      left: "5%",
      "--tx": "40px",
      "--ty": "30px",
      animationDelay: "1.2s",
    },
    {
      top: "65%",
      right: "8%",
      "--tx": "-30px",
      "--ty": "40px",
      animationDelay: "0.3s",
    },
    {
      top: "40%",
      left: "3%",
      "--tx": "25px",
      "--ty": "-30px",
      animationDelay: "1.8s",
    },
    {
      top: "50%",
      right: "4%",
      "--tx": "-35px",
      "--ty": "-25px",
      animationDelay: "0.9s",
    },
  ];

  const cardDelay = (i) => ({
    animation: mounted
      ? `ap-fade-in-up 0.55s ease ${0.1 + i * 0.12}s both`
      : "none",
  });

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(160deg, #fdf6f0 0%, #fff9f5 45%, #f5f0ff 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        fontFamily: "Inter, system-ui, sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background decorative circles */}
      <div
        style={{
          position: "fixed",
          top: -80,
          right: -80,
          width: 320,
          height: 320,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,104,18,0.08) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "fixed",
          bottom: -60,
          left: -60,
          width: 260,
          height: 260,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,104,18,0.06) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Floating particles */}
      {particles.map((p, i) => (
        <Particle key={i} style={p} />
      ))}

      {/* ── Header logo bar ── */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: 52,
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(255,104,18,0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10,
        }}
      >
        <img src={logo} alt="HireCorrecto" style={{ height: 38 }} />
      </div>

      {/* ── Main card ── */}
      <div
        style={{
          marginTop: 60,
          width: "100%",
          maxWidth: 540,
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: 24,
          boxShadow:
            "0 24px 60px rgba(255,104,18,0.1), 0 8px 24px rgba(0,0,0,0.06)",
          border: "1px solid rgba(255,104,18,0.12)",
          overflow: "hidden",
          ...cardDelay(0),
        }}
      >
        {/* Top gradient bar */}
        <div
          style={{
            height: 5,
            background:
              "linear-gradient(90deg, #FF6812 0%, #ff9a5c 50%, #FF6812 100%)",
            backgroundSize: "200% 100%",
            animation: "ap-shimmer 2.5s linear infinite",
          }}
        />

        {/* Card body */}
        <div style={{ padding: "24px 32px 22px", textAlign: "center" }}>
          {/* Shield */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 16,
              ...cardDelay(1),
            }}
          >
            <ShieldIcon />
          </div>

          {/* Badge */}
          <div style={{ marginBottom: 10, ...cardDelay(2) }}>
            <ShimmerBadge text="Assessment Paused" />
          </div>

          {/* Heading */}
          <h1
            style={{
              margin: "0 0 8px",
              fontSize: 26,
              fontWeight: 800,
              color: "#1a1a2e",
              lineHeight: 1.25,
              letterSpacing: "-0.3px",
              ...cardDelay(2),
            }}
          >
            Not Accepting Responses
          </h1>

          {/* Subheading */}
          <p
            style={{
              margin: "0 0 4px",
              fontSize: 15,
              color: "#666",
              lineHeight: 1.65,
              ...cardDelay(3),
            }}
          >
            <strong style={{ color: "#1a1a2e" }}>{companyName}</strong> has
            temporarily paused this assessment. New responses are not being
            accepted right now.
          </p>

          <div style={{ ...cardDelay(3) }}>
            <BounceDots />
          </div>

          {/* Divider */}
          <div
            style={{
              margin: "16px 0",
              height: 1,
              background:
                "linear-gradient(90deg, transparent, #f0f0f0, transparent)",
              ...cardDelay(3),
            }}
          />

          {/* Info rows */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              textAlign: "left",
            }}
          >
            <div style={{ ...cardDelay(4) }}>
              <InfoRow
                icon="⏳"
                text="This is a temporary pause. The assessment may resume soon — please try again later or contact the recruiter."
              />
            </div>
            <div style={{ ...cardDelay(5) }}>
              <InfoRow
                icon="💡"
                text="Your eligibility and profile are still valid. You can return and attempt this assessment once it is reopened."
              />
            </div>
            <div style={{ ...cardDelay(6) }}>
              <InfoRow
                icon="📧"
                text="If you believe this is an error, reach out to the recruiter or HR team via the job listing contact information."
              />
            </div>
          </div>

          {/* Status bar */}
          {/* <div
            style={{
              marginTop: 28,
              padding: "14px 18px",
              background: "linear-gradient(135deg, #fff8f4, #fff3ec)",
              borderRadius: 12,
              border: "1px solid rgba(255,104,18,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              ...cardDelay(7),
            }}
          >
            <div style={{ textAlign: "left" }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  color: "#aaa",
                  fontWeight: 500,
                  marginBottom: 2,
                }}
              >
                Current Status
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#cc4400",
                }}
              >
                🔒 Responses Locked
              </p>
            </div>
            <div
              style={{
                padding: "6px 14px",
                background: "white",
                borderRadius: 20,
                border: "1px solid #f0d0c0",
                fontSize: 12,
                fontWeight: 600,
                color: "#FF6812",
                cursor: "pointer",
                transition: "all 0.2s",
                whiteSpace: "nowrap",
              }}
              onClick={() => window.location.reload()}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#FF6812";
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#fff";
                e.currentTarget.style.color = "#FF6812";
              }}
            >
              🔄 Check Again
            </div>
          </div> */}
        </div>
      </div>

      {/* ── Footer ── */}
      <div
        style={{
          marginTop: 16,
          fontSize: 12,
          color: "#bbb",
          textAlign: "center",
          ...cardDelay(8),
        }}
      >
        Powered by{" "}
        <span style={{ color: "#FF6812", fontWeight: 700 }}>HireCorrecto</span>{" "}
        · Assessment Platform
      </div>
    </div>
  );
}

export default AssessmentPaused;
